import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Typography,
  CircularProgress,
  Switch,
  FormControlLabel,
  Divider,
  Box,
  Alert,
  AlertTitle
} from '@mui/material';
import AddressLookup from '../common/AddressLookup';
import { Property, CreatePropertyDTO, PropertyFormValues } from '../../types/property.types';
import { useLookups } from '../../hooks/useLookups';
import { useClientContext } from '../../context/ClientContext';
import toast from 'react-hot-toast';

interface PropertyFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (property: CreatePropertyDTO) => void;
  initialValues?: Property;
  title?: string;
  clientId: string;
}

// Dark theme TextField styling
const textFieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.2)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.3)',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'primary.main',
    },
    '& input': {
      color: 'text.primary',
    },
    '& textarea': {
      color: 'text.primary',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.7)',
    '&.Mui-focused': {
      color: 'primary.main',
    },
  },
};

const validationSchema = Yup.object({
  name: Yup.string()
    .required('Property name is required')
    .min(2, 'Property name must be at least 2 characters')
    .max(100, 'Property name must be at most 100 characters'),
  address: Yup.object({
    line1: Yup.string()
      .required('Address line 1 is required')
      .max(100, 'Address line 1 must be at most 100 characters'),
    line2: Yup.string()
      .max(100, 'Address line 2 must be at most 100 characters'),
    city: Yup.string()
      .required('City is required')
      .max(50, 'City must be at most 50 characters'),
    county: Yup.string()
      .max(50, 'County must be at most 50 characters'),
    postcode: Yup.string()
      .required('Postcode is required')
      .max(10, 'Postcode must be at most 10 characters'),
  }),
  contactName: Yup.string()
    .required('Contact name is required'),
  contactEmail: Yup.string()
    .email('Invalid email address')
    .required('Contact email is required'),
  contactPhone: Yup.string()
    .required('Contact phone is required'),
  notes: Yup.string()
});

const PropertyForm: React.FC<PropertyFormProps> = ({
  open,
  onClose,
  onSubmit,
  initialValues,
  title = 'Add Property',
  clientId
}) => {
  const { loading: lookupsLoading } = useLookups();
  const { selectedClient } = useClientContext();
  const hasClientAddress = selectedClient?.tradingAddress?.line1;
  const [useClientContact, setUseClientContact] = useState(!initialValues?.contactName);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const formik = useFormik<PropertyFormValues>({
    initialValues: initialValues ? {
      name: initialValues.name,
      address: initialValues.address,
      contactName: initialValues.contactName,
      contactEmail: initialValues.contactEmail,
      contactPhone: initialValues.contactPhone,
      clientId: initialValues.client,
      notes: initialValues.notes || '',
      isCOT: initialValues.isCOT === true
    } : {
      name: '',
      address: {
        line1: '',
        line2: '',
        city: '',
        county: '',
        postcode: '',
        country: 'United Kingdom',
      },
      contactName: selectedClient?.ownerName || '',
      contactEmail: selectedClient?.email || '',
      contactPhone: selectedClient?.phoneNumber || '',
      clientId,
      notes: '',
      isCOT: false
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      setSubmitError(null);
      
      try {
        // Validate required fields explicitly
        if (!values.name || !values.address.line1 || !values.address.city || 
            !values.address.postcode || !values.contactName || 
            !values.contactEmail || !values.contactPhone) {
          throw new Error('Please fill in all required fields');
        }

        await onSubmit({
          ...values,
          clientId: typeof values.clientId === 'string' 
            ? values.clientId 
            : values.clientId._id,
        });
        
        // Show success message
        toast.success(
          `Property "${values.name}" ${initialValues ? 'updated' : 'created'} successfully!`,
          {
            duration: 4000,
            position: 'top-right',
            icon: '✅',
          }
        );
        
        onClose();
      } catch (err: any) {
        console.error('Property save error:', err);
        
        // Build user-friendly error message
        let errorMessage = `Failed to ${initialValues ? 'update' : 'create'} property. `;
        
        if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err.response?.data?.errors) {
          const fieldErrors = err.response.data.errors;
          const errorList = Object.entries(fieldErrors)
            .map(([field, msg]) => `• ${field}: ${msg}`)
            .join('\n');
          errorMessage = `Please fix the following errors:\n${errorList}`;
        } else if (err.response?.status === 400) {
          errorMessage = 'Invalid property data. Please check all required fields and try again.';
        } else if (err.response?.status === 409) {
          errorMessage = 'A property with this information already exists.';
        } else if (err.response?.status === 500) {
          errorMessage = 'Server error occurred. Please try again or contact support if the problem persists.';
        } else if (err.message) {
          errorMessage = err.message;
        } else {
          errorMessage = 'An unexpected error occurred. Please try again.';
        }
        
        setSubmitError(errorMessage);
        
        // Scroll to top to show error
        document.querySelector('[role="dialog"]')?.scrollTo({ top: 0, behavior: 'smooth' });
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  // Effect to update contact details when toggle changes
  useEffect(() => {
    if (useClientContact && selectedClient) {
      console.log('Setting client contact details:', {
        contactName: selectedClient.ownerName,
        contactEmail: selectedClient.email,
        contactPhone: selectedClient.phoneNumber
      });
      formik.setFieldValue('contactName', selectedClient.ownerName || '', false);
      formik.setFieldValue('contactEmail', selectedClient.email || '', false);
      formik.setFieldValue('contactPhone', selectedClient.phoneNumber || '', false);
    }
  }, [useClientContact, selectedClient, formik]);

  const handleContactToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const useClient = event.target.checked;
    setUseClientContact(useClient);
    
    if (useClient && selectedClient) {
      formik.setFieldValue('contactName', selectedClient.ownerName || '');
      formik.setFieldValue('contactEmail', selectedClient.email || '');
      formik.setFieldValue('contactPhone', selectedClient.phoneNumber || '');
    } else {
      formik.setFieldValue('contactName', '');
      formik.setFieldValue('contactEmail', '');
      formik.setFieldValue('contactPhone', '');
    }
  };

  // Debug form validation state
  useEffect(() => {
    if (Object.keys(formik.errors).length > 0) {
      
    }
  }, [formik.errors]);

  if (lookupsLoading) {
    return (
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            backdropFilter: 'blur(12px)',
            backgroundColor: 'rgba(30, 41, 59, 0.95)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '0.75rem',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.1)',
          }
        }}
      >
        <DialogContent>
          <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
            <CircularProgress sx={{ color: 'primary.light' }} />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Form submission handler with improved error display
  const handleFormSubmit = () => {
    
    
    
    // Force validation before submission
    formik.validateForm().then(errors => {
      
      if (Object.keys(errors).length === 0) {
        
        formik.submitForm();
      } else {
        
        // Mark all fields as touched to show validation errors
        Object.keys(formik.values).forEach(key => {
          formik.setFieldTouched(key, true, true);
        });
        // For nested address fields
        ['line1', 'line2', 'city', 'county', 'postcode'].forEach(key => {
          formik.setFieldTouched(`address.${key}`, true, true);
        });
        
        // Create a more user-friendly error message
        let errorMessage = 'Please fix the following errors:';
        if (errors.name) errorMessage += '\n• Property name: ' + errors.name;
        if (errors.address) {
          if (typeof errors.address === 'object') {
            if (errors.address.line1) errorMessage += '\n• Address line 1: ' + errors.address.line1;
            if (errors.address.city) errorMessage += '\n• City: ' + errors.address.city;
            if (errors.address.postcode) errorMessage += '\n• Postcode: ' + errors.address.postcode;
          } else {
            errorMessage += '\n• Address: ' + errors.address;
          }
        }
        if (errors.contactName) errorMessage += '\n• Contact name: ' + errors.contactName;
        if (errors.contactEmail) errorMessage += '\n• Contact email: ' + errors.contactEmail;
        if (errors.contactPhone) errorMessage += '\n• Contact phone: ' + errors.contactPhone;
        
        setSubmitError(errorMessage);
      }
    });
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          backdropFilter: 'blur(12px)',
          backgroundColor: 'rgba(30, 41, 59, 0.95)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: '0.75rem',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.1)',
        }
      }}
    >
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle sx={{ color: 'white', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>{title}</DialogTitle>
        <DialogContent>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2, mt: 2, backgroundColor: 'rgba(211, 47, 47, 0.15)', color: '#ff8a80' }}>
              <AlertTitle>Error</AlertTitle>
              <Typography sx={{ whiteSpace: 'pre-line' }}>{submitError}</Typography>
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="name"
                name="name"
                label="Property Name"
                value={formik.values.name}
                onChange={formik.handleChange}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                sx={textFieldSx}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formik.values.isCOT || false}
                    onChange={(e) => formik.setFieldValue('isCOT', e.target.checked)}
                    name="isCOT"
                  />
                }
                label="Change of Tenancy (COT)"
              />
            </Grid>



            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="subtitle1" color="text.primary">
                  Address
                </Typography>
                {hasClientAddress && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      formik.setFieldValue('address', {
                        line1: selectedClient?.tradingAddress?.line1 || '',
                        line2: selectedClient?.tradingAddress?.line2 || '',
                        city: selectedClient?.tradingAddress?.city || '',
                        county: selectedClient?.tradingAddress?.county || '',
                        postcode: selectedClient?.tradingAddress?.postcode || '',
                        country: selectedClient?.tradingAddress?.country || 'United Kingdom'
                      });
                    }}
                    sx={{
                      borderColor: 'primary.light',
                      color: 'primary.light',
                      '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: 'rgba(59, 130, 246, 0.1)',
                      }
                    }}
                  >
                    Copy from Trading Address
                  </Button>
                )}
              </Box>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12}>
              <AddressLookup
                value={formik.values.address}
                onChange={(address) => {
                  formik.setFieldValue('address', address, true);
                }}
                error={formik.errors.address}
                touched={formik.touched.address}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom color="text.primary">
                Contact Details
              </Typography>
              <Divider sx={{ mb: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={useClientContact}
                    onChange={handleContactToggle}
                    name="useClientContact"
                  />
                }
                label="Use Client Contact Details"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                id="contactName"
                name="contactName"
                label="Contact Name"
                value={formik.values.contactName}
                onChange={formik.handleChange}
                error={formik.touched.contactName && Boolean(formik.errors.contactName)}
                helperText={formik.touched.contactName && formik.errors.contactName}
                disabled={useClientContact}
                sx={textFieldSx}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="contactEmail"
                name="contactEmail"
                label="Contact Email"
                value={formik.values.contactEmail}
                onChange={formik.handleChange}
                error={formik.touched.contactEmail && Boolean(formik.errors.contactEmail)}
                helperText={formik.touched.contactEmail && formik.errors.contactEmail}
                disabled={useClientContact}
                sx={textFieldSx}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="contactPhone"
                name="contactPhone"
                label="Contact Phone"
                value={formik.values.contactPhone}
                onChange={formik.handleChange}
                error={formik.touched.contactPhone && Boolean(formik.errors.contactPhone)}
                helperText={formik.touched.contactPhone && formik.errors.contactPhone}
                disabled={useClientContact}
                sx={textFieldSx}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                id="notes"
                name="notes"
                label="Notes"
                multiline
                rows={4}
                value={formik.values.notes}
                onChange={formik.handleChange}
                sx={textFieldSx}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogContent sx={{ pt: 0 }}>
          {submitError && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(239, 68, 68, 0.2)', borderRadius: 1, border: '1px solid rgba(239, 68, 68, 0.3)' }}>
              <Typography color="error.light" sx={{ whiteSpace: 'pre-line' }}>
                {submitError}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', p: 2 }}>
          <Button 
            onClick={onClose} 
            disabled={isSubmitting}
            sx={{
              color: 'white',
              borderColor: 'rgba(255, 255, 255, 0.2)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderColor: 'rgba(255, 255, 255, 0.3)',
              }
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            disabled={isSubmitting}
            onClick={() => {
              
              // This ensures the form submission is triggered even if the normal form submit event isn't firing
              if (!isSubmitting) {
                handleFormSubmit();
              }
            }}
            sx={{
              background: 'linear-gradient(45deg, #3b82f6 30%, #60a5fa 90%)',
              boxShadow: '0 3px 5px 2px rgba(59, 130, 246, .3)',
              '&:hover': {
                background: 'linear-gradient(45deg, #2563eb 30%, #3b82f6 90%)',
              }
            }}
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default PropertyForm;
